import { useEffect, useState } from 'react';

import { TableCheckbox } from '@components/Table/Checkbox';
import { TableRowSelection } from 'antd/es/table/interface';
import type { RowSelectionParams } from '@components/Table/VirtualTable';

export interface RowForceUnselectParams {
    forceUnSelectItemIds: string[];
    resetUnSelectItemIds(): void;
}

interface Props {
    dataSource: readonly any[];
    rowSelectionParent?: RowSelectionParams;
    rowForceUnselectParams?: RowForceUnselectParams;
}

interface Data {
    rowSelection: TableRowSelection<any>;
}

export function useCheckboxes({ dataSource, rowSelectionParent, rowForceUnselectParams }: Props): Data {
    const [selectedRowKeys, setSelectedRowKeys] = useState<any>(rowSelectionParent?.selectedRowKeys ?? []);
    const [isAllChecked, setIsAllChecked] = useState<boolean>(false);

    useEffect(() => {
        if (!!rowForceUnselectParams?.forceUnSelectItemIds?.length) {
            const filteredRows = selectedRowKeys.filter(
                (id: string) => !rowForceUnselectParams?.forceUnSelectItemIds.includes(id),
            );
            setSelectedRowKeys(filteredRows);
            rowSelectionParent?.setSelectedRowsKeys(filteredRows);
            rowForceUnselectParams.resetUnSelectItemIds();
        }
    }, [rowForceUnselectParams, rowForceUnselectParams?.forceUnSelectItemIds, rowSelectionParent, selectedRowKeys]);

    function getRowSelection(): TableRowSelection<any> {
        return {
            columnWidth: 50,
            selectedRowKeys,
            onChange: onSelectAll,
            onCell: () => ({ onClick: event => event.stopPropagation() }),
            columnTitle: (
                <TableCheckbox
                    isChecked={isAllChecked}
                    className={'virtual_table_core_checkbox'}
                    onChange={() => {
                        onSelectAll();
                    }}
                />
            ),
            renderCell: (value, record) => {
                return (
                    <TableCheckbox
                        disabled={record.disableRowSelect}
                        isChecked={value}
                        className={'virtual_table_core_checkbox'}
                        onChange={() => {
                            onChange(value, record);
                        }}
                    />
                );
            },
        };
    }

    function onSelectAll(): void {
        if (isAllChecked) {
            setSelectedRowKeys([]);
            rowSelectionParent?.setSelectedRowsKeys([]);
            setIsAllChecked(false);
        } else {
            const activeRows = dataSource.filter(item => !item.disableRowSelect);
            const keys = activeRows.map(item => item.key);
            setSelectedRowKeys(keys);
            rowSelectionParent?.setSelectedRowsKeys(keys);
            setIsAllChecked(true);
        }
    }

    function onChange(_: boolean, record: any): void {
        if (selectedRowKeys.includes(record.key)) {
            const filteredRows = selectedRowKeys.filter((key: string) => key !== record.key);
            setSelectedRowKeys(filteredRows);
            rowSelectionParent?.setSelectedRowsKeys(filteredRows);
            if (isAllChecked) {
                setIsAllChecked(false);
            }
        } else {
            setSelectedRowKeys([...selectedRowKeys, record.key]);
            rowSelectionParent?.setSelectedRowsKeys([...selectedRowKeys, record.key]);
            if (selectedRowKeys.length + 1 === dataSource.length) {
                setIsAllChecked(true);
            }
        }
    }

    return {
        rowSelection: getRowSelection(),
    };
}
